import { ComboboxItem } from '@oncolens/oncolens-ui/lib/components/FormControls/Combobox/Combobox';
import { PagedApiResponse, Pagination } from '@oncolens/oncolens-ui/lib/dto/api/ApiResponse';
import { UseQueryOptions, useQuery, useQueryClient } from 'react-query';
import client from '../../../utils/httpClient';
import { QipKeys } from './qip.keys';
import { useCallback } from 'react';

export interface Question {
  id: string;
  name: string;
  displayName: string;
  type: string;
  status: QipQuestionStatus;
}

export interface QuestionWithGroups {
  id: string;
  name: string;
  displayName: string;
  status: QipQuestionStatus;
  version: number;
  type: string;
  updatedDate: Date;
  associatedGroups: string[];
}

export interface Choice {
  id: string;
  name: string;
  index: number;
  qipMetricQuestionId: string;
}

export interface Condition {
  id: string;
  groupId: string;
  questionId: string;
  choiceToTriggerId: string;
  questionToShowId: string;
}

export interface Group {
  id: string;
  name: string;
  version: number;
  status: QipQuestionStatus;
  organizationIds: string[];
  questionIds: string[];
  conditions: Condition[];
  updatedDate: Date;
}

export interface QuestionFormValues {
  name: string;
  displayName: string;
  type: string;
  status: QipQuestionStatus;
}

export interface GroupFormValues {
  name: string;
  displayName: string;
  status: QipQuestionStatus;
  organizationIds: ComboboxItem[] | null;
  questionIds: ComboboxItem[] | null;
  selectAllOrganizations: boolean;
}

export type QipQuestionStatus = 'Active' | 'Inactive';

// Questions

export const getQipQuestions = () =>
  client.get<Question[]>('/qipmetricquestions').then(({ data }) => data);

export const getQuestionById = (id: string) =>
  client.get<Question>(`/qipmetricquestions/${id}`).then(({ data }) => data);

export const useGetQipQuestions = (options?: UseQueryOptions<Question[]>) =>
  useQuery(QipKeys.questions(), () => getQipQuestions(), options);

export const useQuestionById = (id: string, options?: UseQueryOptions<Question>) =>
  useQuery([...QipKeys.question(id), id], () => getQuestionById(id), options);

export const updateQuestion = (id: string, data: Question) =>
  client.put(`/qipmetricquestions/${id}`, data);

export const createQuestion = (data: Omit<Question, 'id'>) =>
  client.post('/qipmetricquestions', data);

// Choices

export const getQipChoices = () =>
  client.get<Choice[]>('/qipquestionchoices').then(({ data }) => data);

export const getChoiceById = (id: string) =>
  client.get<Choice>(`/qipquestionchoices/${id}`).then(({ data }) => data);

export const useGetQipChoices = (options?: UseQueryOptions<Choice[]>) =>
  useQuery(QipKeys.choices(), () => getQipChoices(), options);

export const useChoiceById = (id: string, options?: UseQueryOptions<Choice>) =>
  useQuery([...QipKeys.choice(id), id], () => getChoiceById(id), options);

export function useInvalidateConditions() {
  const queryClient = useQueryClient();

  return useCallback(() => queryClient.invalidateQueries(QipKeys.conditions()), [client]);
}

export function useInvalidateQuestions() {
  const queryClient = useQueryClient();

  return useCallback(() => queryClient.invalidateQueries(QipKeys.questions()), [client]);
}

// Groups

export function getQipGroups(queryString: string) {
  const query = queryString ? `?${queryString}` : '';
  return client.get(`/qipmetricquestiongroups${query}`).then((response) => {
    const data = response.data as Group[];

    return {
      data,
      pagination: JSON.parse(response.headers['x-pagination']) as Pagination,
    } as PagedApiResponse<Group>;
  });
}

export const getGroupById = (id: string) =>
  client.get<Group>(`/qipmetricquestiongroups/${id}`).then(({ data }) => data);

export const useGroupById = (id: string, options?: UseQueryOptions<Group>) =>
  useQuery([...QipKeys.question(id), id], () => getGroupById(id), options);

export const updateGroup = (id: string, data: Omit<Group, 'version'>) =>
  client.put(`/qipmetricquestiongroups/${id}`, data);

export const createGroup = (data: Omit<Group, 'id' | 'version'>) =>
  client.post('/qipmetricquestiongroups', data);

export const getQipConditions = (groupId: string) =>
  client.get<Condition[]>(`/qipmetricgroupconditions/group/${groupId}`).then(({ data }) => data);

export const useGetConditions = (groupId: string, options?: UseQueryOptions<Condition[]>) =>
  useQuery([...QipKeys.conditions(), groupId], () => getQipConditions(groupId), options);

// Questions with Groups
export function getQipQuestionsWithGroups(queryString: string) {
  const query = queryString ? `?${queryString}` : '';
  return client.get(`/qipmetricquestions/list${query}`).then((response) => {
    const data = response.data as QuestionWithGroups[];

    return {
      data,
      pagination: JSON.parse(response.headers['x-pagination']) as Pagination,
    } as PagedApiResponse<QuestionWithGroups>;
  });
}
